// src/components/AdditionalItemsModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

// Available icons for selection
const AVAILABLE_ICONS = [
    { id: 'controller', emoji: '🎮', label: 'Control' },
    { id: 'game', emoji: '🎯', label: 'Juego' },
    { id: 'headset', emoji: '🎧', label: 'Audífonos' },
    { id: 'cable', emoji: '🔌', label: 'Cable' },
    { id: 'charger', emoji: '🔋', label: 'Cargador' },
    { id: 'case', emoji: '💼', label: 'Estuche' },
    { id: 'memory', emoji: '💾', label: 'Memoria' },
    { id: 'disc', emoji: '💿', label: 'Disco' },
    { id: 'box', emoji: '📦', label: 'Caja' },
    { id: 'manual', emoji: '📖', label: 'Manual' },
    { id: 'accessory', emoji: '🔧', label: 'Accesorio' },
    { id: 'other', emoji: '⭐', label: 'Otro' }
];

// Icon Selector Component
function IconSelector({ selectedIcon, onSelect, disabled }) {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (icon) => {
        onSelect(icon);
        setIsOpen(false);
    };

    const currentIcon = AVAILABLE_ICONS.find(i => i.id === selectedIcon) || AVAILABLE_ICONS[0];

    return (
        <div className="icon-selector">
            <label className="form-label">Ícono</label>
            <div className="icon-selector-trigger" onClick={() => !disabled && setIsOpen(!isOpen)}>
                <span className="selected-icon">{currentIcon.emoji}</span>
                <span className="selected-icon-label">{currentIcon.label}</span>
                <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && (
                <div className="icon-selector-dropdown">
                    {AVAILABLE_ICONS.map(icon => (
                        <div
                            key={icon.id}
                            className={`icon-option ${icon.id === selectedIcon ? 'selected' : ''}`}
                            onClick={() => handleSelect(icon.id)}
                        >
                            <span className="icon-emoji">{icon.emoji}</span>
                            <span className="icon-label">{icon.label}</span>
                        </div>
                    ))}
                </div>
            )}

            <style jsx>{`
        .icon-selector {
          position: relative;
          margin-bottom: 1rem;
        }
        
        .icon-selector-trigger {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .icon-selector-trigger:hover {
          border-color: var(--primary-light);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .selected-icon {
          font-size: 1.5rem;
        }
        
        .selected-icon-label {
          flex: 1;
          font-weight: 500;
          color: var(--text-dark);
        }
        
        .dropdown-arrow {
          color: var(--text-light);
          font-size: 0.8rem;
        }
        
        .icon-selector-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 0.5rem;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
        }
        
        .icon-option {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .icon-option:hover {
          background-color: rgba(99, 102, 241, 0.1);
        }
        
        .icon-option.selected {
          background-color: rgba(99, 102, 241, 0.15);
          font-weight: 600;
        }
        
        .icon-emoji {
          font-size: 1.5rem;
        }
        
        .icon-label {
          color: var(--text-dark);
        }
      `}</style>
        </div>
    );
}

// State Item Component (for managing individual states within an item)
function StateItem({ state, onEdit, onDelete }) {
    return (
        <div className="state-item">
            <div className="state-info">
                <div className="state-name">{state.name}</div>
                <div className="state-details">
                    <span className="state-value">${state.value.toLocaleString()}</span>
                    <span className="state-max">Máx: {state.max}</span>
                </div>
            </div>
            <div className="state-actions">
                <button className="btn-icon btn-edit" onClick={() => onEdit(state)} title="Editar">
                    ✏️
                </button>
                <button className="btn-icon btn-delete" onClick={() => onDelete(state.id)} title="Eliminar">
                    🗑️
                </button>
            </div>

            <style jsx>{`
        .state-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #F9FAFB;
          border-radius: 6px;
          border: 1px solid #E5E7EB;
          transition: all 0.2s ease;
        }
        
        .state-item:hover {
          border-color: var(--primary-light);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        
        .state-info {
          flex: 1;
        }
        
        .state-name {
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 0.25rem;
        }
        
        .state-details {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
        }
        
        .state-value {
          color: var(--secondary-color);
          font-weight: 600;
        }
        
        .state-max {
          color: var(--text-light);
        }
        
        .state-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .btn-icon {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .btn-icon:hover {
          background: rgba(99, 102, 241, 0.1);
          transform: scale(1.1);
        }
      `}</style>
        </div>
    );
}

// Additional Item Card Component
function AdditionalItemCard({ item, onEdit, onDelete, onManageStates }) {
    const icon = AVAILABLE_ICONS.find(i => i.id === item.icon) || AVAILABLE_ICONS[0];

    return (
        <div className="additional-item-card">
            <div className="item-header">
                <div className="item-icon-name">
                    <span className="item-icon">{icon.emoji}</span>
                    <span className="item-name">{item.name}</span>
                </div>
                <div className="item-badge">
                    {item.states.length} {item.states.length === 1 ? 'estado' : 'estados'}
                </div>
            </div>

            <div className="item-actions">
                <button className="btn btn-outline btn-sm" onClick={() => onManageStates(item)}>
                    📋 Gestionar Estados
                </button>
                <button className="btn-icon btn-edit" onClick={() => onEdit(item)} title="Editar nombre/ícono">
                    ✏️
                </button>
                <button className="btn-icon btn-delete" onClick={() => onDelete(item.id)} title="Eliminar">
                    🗑️
                </button>
            </div>

            <style jsx>{`
        .additional-item-card {
          background: white;
          border: 2px solid #E5E7EB;
          border-radius: 8px;
          padding: 1rem;
          transition: all 0.3s ease;
        }
        
        .additional-item-card:hover {
          border-color: var(--primary-light);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        
        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .item-icon-name {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .item-icon {
          font-size: 2rem;
        }
        
        .item-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-dark);
        }
        
        .item-badge {
          background: var(--primary-color);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .item-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        
        .btn-sm {
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          flex: 1;
        }
      `}</style>
        </div>
    );
}

// Main Modal Component
export default function AdditionalItemsModal({
    isOpen,
    onClose,
    retailer,
    brand,
    category
}) {
    // State
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [view, setView] = useState('list'); // 'list', 'add-item', 'edit-item', 'manage-states'
    const [currentItem, setCurrentItem] = useState(null);
    const [currentState, setCurrentState] = useState(null);

    // Form states
    const [itemName, setItemName] = useState('');
    const [itemIcon, setItemIcon] = useState('controller');
    const [stateName, setStateName] = useState('');
    const [stateValue, setStateValue] = useState('');
    const [stateMax, setStateMax] = useState('');

    // API Configuration
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod';
    const API_ENDPOINT = `${API_BASE_URL}/v1/additional_items`;

    // Get authentication headers
    const getAuthHeaders = async () => {
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                throw new Error('No se pudo obtener el token de autenticación');
            }

            return {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json'
            };
        } catch (error) {
            console.error('Error al obtener headers de autenticación:', error);
            throw error;
        }
    };

    // Fetch additional items from API
    const fetchAdditionalItems = async () => {
        if (!retailer || !brand || !category) return;

        setLoading(true);
        setError('');

        try {
            const headers = await getAuthHeaders();
            const params = new URLSearchParams({
                retailer,
                brand,
                category
            });

            const response = await axios.get(`${API_ENDPOINT}?${params}`, { headers });

            if (response.data.success) {
                setItems(response.data.data.items || []);
            }
        } catch (err) {
            console.error('Error al cargar items adicionales:', err);
            if (err.response?.status === 404) {
                // No hay items todavía, está bien
                setItems([]);
            } else {
                setError('Error al cargar los items adicionales. Intenta de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Save additional items to API
    const saveAdditionalItems = async (updatedItems) => {
        if (!retailer || !brand || !category) return { success: false };

        setSaving(true);
        setError('');

        try {
            const headers = await getAuthHeaders();
            const payload = {
                retailer,
                brand,
                category,
                items: updatedItems
            };

            const response = await axios.post(API_ENDPOINT, payload, { headers });

            if (response.data.success) {
                const freshItems = response.data.data.items;
                setItems(freshItems);
                return { success: true, items: freshItems };
            }
        } catch (err) {
            console.error('Error al guardar items adicionales:', err);
            setError('Error al guardar los items. Intenta de nuevo.');
            return { success: false };
        } finally {
            setSaving(false);
        }
    };

    // Load items when modal opens
    useEffect(() => {
        if (isOpen && retailer && brand && category) {
            fetchAdditionalItems();
        }

        if (!isOpen) {
            setView('list');
            setCurrentItem(null);
            setCurrentState(null);
            resetForms();
            setError('');
        }
    }, [isOpen, retailer, brand, category]);

    const resetForms = () => {
        setItemName('');
        setItemIcon('controller');
        setStateName('');
        setStateValue('');
        setStateMax('');
    };

    // Item CRUD operations
    const handleAddItem = async () => {
        if (!itemName.trim()) return;

        const selectedIconObj = AVAILABLE_ICONS.find(i => i.id === itemIcon) || AVAILABLE_ICONS[0];

        const newItem = {
            id: Date.now().toString(),
            name: itemName.trim(),
            icon: selectedIconObj.emoji,  // Guardar el emoji
            icon_id: selectedIconObj.id,  // Guardar el ID
            states: []
        };

        const updatedItems = [...items, newItem];
        const result = await saveAdditionalItems(updatedItems);

        if (result.success) {
            resetForms();
            setView('list');
        }
    };

    const handleEditItem = (item) => {
        setCurrentItem(item);
        setItemName(item.name);
        // Si tiene icon_id, usarlo; si no, intentar encontrar por emoji
        setItemIcon(item.icon_id || AVAILABLE_ICONS.find(i => i.emoji === item.icon)?.id || 'controller');
        setView('edit-item');
    };

    const handleUpdateItem = async () => {
        if (!itemName.trim() || !currentItem) return;

        const selectedIconObj = AVAILABLE_ICONS.find(i => i.id === itemIcon) || AVAILABLE_ICONS[0];

        const updatedItems = items.map(item =>
            item.id === currentItem.id
                ? {
                    ...item,
                    name: itemName.trim(),
                    icon: selectedIconObj.emoji,  // Actualizar emoji
                    icon_id: selectedIconObj.id   // Actualizar ID
                  }
                : item
        );

        const result = await saveAdditionalItems(updatedItems);

        if (result.success) {
            resetForms();
            setCurrentItem(null);
            setView('list');
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (window.confirm('¿Estás seguro de eliminar este item y todos sus estados?')) {
            const updatedItems = items.filter(item => item.id !== itemId);
            await saveAdditionalItems(updatedItems);
        }
    };

    // State CRUD operations
    const handleManageStates = (item) => {
        setCurrentItem(item);
        setView('manage-states');
    };

    const handleAddState = async () => {
        if (!stateName.trim() || !stateValue || !stateMax) return;

        const newState = {
            id: Date.now().toString(),
            name: stateName.trim(),
            value: parseInt(stateValue),
            max: parseInt(stateMax)
        };

        const updatedItems = items.map(item =>
            item.id === currentItem.id
                ? { ...item, states: [...item.states, newState] }
                : item
        );

        const result = await saveAdditionalItems(updatedItems);

        if (result.success) {
            // Actualizar currentItem con los datos frescos de la API
            const refreshedItem = result.items.find(item => item.id === currentItem.id);
            if (refreshedItem) {
                setCurrentItem(refreshedItem);
            }

            setStateName('');
            setStateValue('');
            setStateMax('');
        }
    };

    const handleEditState = (state) => {
        setCurrentState(state);
        setStateName(state.name);
        setStateValue(state.value.toString());
        setStateMax(state.max.toString());
    };

    const handleUpdateState = async () => {
        if (!stateName.trim() || !stateValue || !stateMax || !currentState) return;

        const updatedItems = items.map(item =>
            item.id === currentItem.id
                ? {
                    ...item,
                    states: item.states.map(s =>
                        s.id === currentState.id
                            ? { ...s, name: stateName.trim(), value: parseInt(stateValue), max: parseInt(stateMax) }
                            : s
                    )
                }
                : item
        );

        const result = await saveAdditionalItems(updatedItems);

        if (result.success) {
            // Actualizar currentItem con los datos frescos de la API
            const refreshedItem = result.items.find(item => item.id === currentItem.id);
            if (refreshedItem) {
                setCurrentItem(refreshedItem);
            }

            setCurrentState(null);
            setStateName('');
            setStateValue('');
            setStateMax('');
        }
    };

    const handleDeleteState = async (stateId) => {
        if (window.confirm('¿Estás seguro de eliminar este estado?')) {
            const updatedItems = items.map(item =>
                item.id === currentItem.id
                    ? { ...item, states: item.states.filter(s => s.id !== stateId) }
                    : item
            );

            const result = await saveAdditionalItems(updatedItems);

            if (result.success) {
                // Actualizar currentItem con los datos frescos de la API
                const refreshedItem = result.items.find(item => item.id === currentItem.id);
                if (refreshedItem) {
                    setCurrentItem(refreshedItem);
                }
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content additional-items-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <div>
                        <h3>🧩 Adicionales</h3>
                        <p className="modal-subtitle">
                            {retailer} → {brand} → {category}
                        </p>
                    </div>
                    <button className="btn-close" onClick={onClose}>✕</button>
                </div>

                {/* Body */}
                <div className="modal-body">
                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Cargando items adicionales...</p>
                        </div>
                    ) : (
                        <>
                            {/* List View */}
                            {view === 'list' && (
                                <>
                                    <div className="section-header">
                                        <h4>Items Adicionales</h4>
                                        <button className="btn btn-primary" onClick={() => setView('add-item')} disabled={saving}>
                                            ➕ Agregar Item
                                        </button>
                                    </div>

                                    {items.length === 0 ? (
                                <div className="empty-state">
                                    <p>📦 No hay items adicionales configurados</p>
                                    <p className="empty-hint">Agrega items como controles, juegos, cables, etc.</p>
                                </div>
                            ) : (
                                <div className="items-grid">
                                    {items.map(item => (
                                        <AdditionalItemCard
                                            key={item.id}
                                            item={item}
                                            onEdit={handleEditItem}
                                            onDelete={handleDeleteItem}
                                            onManageStates={handleManageStates}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Add/Edit Item View */}
                    {(view === 'add-item' || view === 'edit-item') && (
                        <>
                            <div className="section-header">
                                <h4>{view === 'add-item' ? '➕ Agregar Item' : '✏️ Editar Item'}</h4>
                                <button className="btn btn-outline" onClick={() => { setView('list'); resetForms(); setCurrentItem(null); }}>
                                    ← Volver
                                </button>
                            </div>

                            <div className="form-container">
                                <div className="form-group">
                                    <label className="form-label">Nombre del Item</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={itemName}
                                        onChange={(e) => setItemName(e.target.value)}
                                        placeholder="Ej: Control, Juego, Cable HDMI..."
                                        autoFocus
                                    />
                                </div>

                                <IconSelector
                                    selectedIcon={itemIcon}
                                    onSelect={setItemIcon}
                                />

                                <button
                                    className="btn btn-primary btn-block"
                                    onClick={view === 'add-item' ? handleAddItem : handleUpdateItem}
                                    disabled={!itemName.trim()}
                                >
                                    {view === 'add-item' ? '✓ Crear Item' : '✓ Guardar Cambios'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Manage States View */}
                    {view === 'manage-states' && currentItem && (
                        <>
                            <div className="section-header">
                                <h4>
                                    {AVAILABLE_ICONS.find(i => i.id === currentItem.icon)?.emoji} {currentItem.name} - Estados
                                </h4>
                                <button className="btn btn-outline" onClick={() => { setView('list'); setCurrentItem(null); resetForms(); }}>
                                    ← Volver
                                </button>
                            </div>

                            {/* Add/Edit State Form */}
                            <div className="state-form">
                                <h5>{currentState ? '✏️ Editar Estado' : '➕ Agregar Estado'}</h5>
                                <div className="form-row">
                                    <div className="form-group" style={{ flex: 2 }}>
                                        <label className="form-label">Estado</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={stateName}
                                            onChange={(e) => setStateName(e.target.value)}
                                            placeholder="Ej: Excelente, Bueno, Regular..."
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label className="form-label">Valor ($)</label>
                                        <input
                                            type="number"
                                            className="form-input"
                                            value={stateValue}
                                            onChange={(e) => setStateValue(e.target.value)}
                                            placeholder="100000"
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label className="form-label">Máximo</label>
                                        <input
                                            type="number"
                                            className="form-input"
                                            value={stateMax}
                                            onChange={(e) => setStateMax(e.target.value)}
                                            placeholder="5"
                                            min="1"
                                        />
                                    </div>
                                </div>
                                <div className="form-actions">
                                    {currentState && (
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => {
                                                setCurrentState(null);
                                                setStateName('');
                                                setStateValue('');
                                                setStateMax('');
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-primary"
                                        onClick={currentState ? handleUpdateState : handleAddState}
                                        disabled={!stateName.trim() || !stateValue || !stateMax}
                                    >
                                        {currentState ? '✓ Actualizar' : '➕ Agregar'}
                                    </button>
                                </div>
                            </div>

                            {/* States List */}
                            <div className="states-list">
                                <h5>Estados Configurados ({currentItem.states.length})</h5>
                                {currentItem.states.length === 0 ? (
                                    <p className="empty-hint">No hay estados configurados. Agrega al menos uno.</p>
                                ) : (
                                    <div className="states-container">
                                        {currentItem.states
                                            .sort((a, b) => b.value - a.value) // Ordenar de mayor a menor por valor
                                            .map(state => (
                                            <StateItem
                                                key={state.id}
                                                state={state}
                                                onEdit={handleEditState}
                                                onDelete={handleDeleteState}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="btn btn-outline" onClick={onClose}>
                        Cerrar
                    </button>
                </div>
            </div>

            <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease-out;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          width: 90%;
          max-width: 900px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          animation: slideUp 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem 2rem;
          border-bottom: 2px solid #E5E7EB;
        }
        
        .modal-header h3 {
          margin: 0;
          color: var(--primary-color);
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .modal-subtitle {
          margin: 0.5rem 0 0 0;
          color: var(--text-light);
          font-size: 0.875rem;
        }
        
        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--text-light);
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .btn-close:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #EF4444;
        }
        
        .modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 2rem;
        }

        .error-message {
          background-color: #FEE2E2;
          border: 1px solid #FCA5A5;
          color: #991B1B;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          font-weight: 500;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: var(--text-light);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #E5E7EB;
          border-top-color: var(--primary-color);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .modal-footer {
          padding: 1rem 2rem;
          border-top: 2px solid #E5E7EB;
          display: flex;
          justify-content: flex-end;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        
        .section-header h4 {
          margin: 0;
          color: var(--text-dark);
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .section-header h5 {
          margin: 0;
          color: var(--text-dark);
          font-size: 1rem;
          font-weight: 600;
        }
        
        .items-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--text-light);
        }
        
        .empty-state p:first-child {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .empty-hint {
          font-size: 0.875rem;
          color: var(--text-light);
          margin-top: 0.5rem;
        }
        
        .form-container {
          max-width: 500px;
          margin: 0 auto;
        }
        
        .btn-block {
          width: 100%;
          margin-top: 1rem;
        }
        
        .state-form {
          background: #F9FAFB;
          border: 2px solid #E5E7EB;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .state-form h5 {
          margin: 0 0 1rem 0;
          color: var(--primary-color);
        }
        
        .states-list h5 {
          margin: 0 0 1rem 0;
          color: var(--text-dark);
        }
        
        .states-container {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        @media (max-width: 768px) {
          .modal-content {
            width: 95%;
            max-height: 95vh;
          }
          
          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 1rem;
          }
          
          .items-grid {
            grid-template-columns: 1fr;
          }
          
          .form-row {
            flex-direction: column;
          }
        }
      `}</style>
        </div>
    );
}
